import React, { forwardRef } from 'react'

import { Minus, Plus } from '../icons'
import { IconProps } from '../icons/types'
import { ExternalProps, TreeItemProps } from './TreeItem'

type Props = {
  /** Class name applied to the treeview root */
  className?: string
  /** Icon used for expanding an element */
  expandIcon?: React.FC<IconProps>
  /** Icon used for collapsing an element */
  collapseIcon?: React.FC<IconProps>
  /** Children of the tree node */
  children:
    | React.ReactElement<ExternalProps>
    | React.ReactElement<ExternalProps>[]
  /** Property called whenever a tree item is expanded. Will only be called if the tree item does
   * not have an onChanged property provided externally.
   */
  onChange?: (itemKey: string, isExpanded: boolean) => void
}

type RefProp = {
  /** Ref for the tree root. Useful to be able to measure the component's dimensions. */
  rootRef: React.ForwardedRef<HTMLDivElement>
}

/** Tree view element containing any number of tree items. */
const InternalTreeView: React.FC<Props & RefProp> = ({
  className,
  children,
  rootRef,
  onChange,
  ...other
}) => {
  return (
    <div className={className} ref={rootRef} data-testid="treeView">
      {React.Children.toArray(children).map((child) => {
        return (
          child &&
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          'props' in child &&
          React.cloneElement(child as React.ReactElement<TreeItemProps>, {
            ...other,
            onChange: child.props.onChange ?? onChange,
          })
        )
      })}
    </div>
  )
}

InternalTreeView.defaultProps = {
  expandIcon: Plus,
  collapseIcon: Minus,
}

const TreeView = forwardRef<HTMLDivElement, Props>((props, ref) => (
  <InternalTreeView rootRef={ref} {...props} />
))

TreeView.displayName = 'TreeView'

export default TreeView
